import type { ReactNode } from "react";

import type { Translate } from "@similarweb/i18n-provider";
import { TextCell, TruncatedTextCell, type ColumnType, type ColumnsType } from "@similarweb/pro-logic-components";

import { compareLocaleStrings, stringSorter, type SortOrder } from "app/pages/sales-intelligence/utils/sort";

import type { TeamUser } from "../../types/teams.types";
import { teamMemberCountryLabel } from "../../utils/teamMemberCountryLabel";

export type AccountUserTableRow = TeamUser;

type AccountUserColumnKey = keyof Pick<TeamUser, "name" | "email" | "jobTitle" | "country">;

type AccountUserColumnConfig<TRow extends TeamUser, K extends AccountUserColumnKey> = {
    title: string;
    sorter: (a: TRow, b: TRow, sortOrder?: SortOrder) => number;
    width: number;
    render: (value: TRow[K], record: TRow, index: number) => ReactNode;
};

const createAccountUserColumn = <TRow extends TeamUser, K extends AccountUserColumnKey>(
    dataIndex: K,
    config: AccountUserColumnConfig<TRow, K>,
): ColumnType<TRow> =>
    ({
        dataIndex,
        ...config,
    }) as unknown as ColumnType<TRow>;

type AccountUserColumnsMap<TRow extends TeamUser> = {
    name: ColumnsType<TRow>[number];
    email: ColumnsType<TRow>[number];
    jobTitle: ColumnsType<TRow>[number];
    country: ColumnsType<TRow>[number];
};

export const getAccountUserColumnsMap = <TRow extends TeamUser>(translate: Translate): AccountUserColumnsMap<TRow> => ({
    name: createAccountUserColumn<TRow, "name">("name", {
        title: translate("si.management.teams.detail.column.user_name"),
        sorter: stringSorter("name"),
        width: 220,
        render: (_value, record, _index) => <TextCell>{record.name ?? ""}</TextCell>,
    }),
    email: createAccountUserColumn<TRow, "email">("email", {
        title: translate("si.management.teams.detail.column.email"),
        sorter: stringSorter("email"),
        width: 280,
        render: (_value, record, _index) => <TruncatedTextCell>{record.email ?? ""}</TruncatedTextCell>,
    }),
    jobTitle: createAccountUserColumn<TRow, "jobTitle">("jobTitle", {
        title: translate("si.management.teams.detail.column.role"),
        sorter: stringSorter("jobTitle"),
        width: 250,
        render: (_value, record, _index) => <TruncatedTextCell>{record.jobTitle ?? ""}</TruncatedTextCell>,
    }),
    country: createAccountUserColumn<TRow, "country">("country", {
        title: translate("si.management.teams.detail.column.country"),
        sorter: (a, b, sortOrder) =>
            compareLocaleStrings(teamMemberCountryLabel(a.country), teamMemberCountryLabel(b.country), sortOrder),
        width: 160,
        render: (_value, record, _index) => <TextCell>{teamMemberCountryLabel(record.country)}</TextCell>,
    }),
});

export const getAccountUserColumns = <TRow extends TeamUser>(translate: Translate): ColumnsType<TRow> => {
    const { name, email, jobTitle, country } = getAccountUserColumnsMap<TRow>(translate);
    return [name, email, jobTitle, country];
};
